import BellIcon from '@assets/icons/bell.svg?react'
import { toastFailure } from '@utils'
import cn from 'classnames'
import {
  useGetUnreadTicketsQuery,
  useMarkActivityAsReadMutation,
  useMarkAllActivitiesAsReadMutation,
  useMarkTicketAsReadMutation
} from 'mmfintech-backend-api'
import type { MerchantActivityResponse } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay, MMButton } from 'mmfintech-portal-commons'
import moment from 'moment'
import { useMemo, useState } from 'react'
import CoreLoader from '../CoreLoader'
import { NotificationItem } from './NotificationItem'
import styles from './NotificationsPanel.module.scss'
import { SupportBlock } from './SupportBlock'
import { useNotificationsList } from './useNotificationsList'
import { useNotificationsUnreadCount } from './useNotificationsUnreadCount'

const DATE_FORMAT = 'DD.MM.YYYY'

const groupByDay = (items: MerchantActivityResponse[]): [string, MerchantActivityResponse[]][] => {
  const groups = new Map<string, MerchantActivityResponse[]>()
  for (const item of items) {
    const day = moment(item.time).format(DATE_FORMAT)
    groups.set(day, [...(groups.get(day) ?? []), item])
  }
  return Array.from(groups.entries())
}

export const NotificationsPanel = () => {
  const list = useNotificationsList()
  const { total: unreadTotal } = useNotificationsUnreadCount()
  const { data: unreadTickets } = useGetUnreadTicketsQuery()

  const [markActivityAsRead] = useMarkActivityAsReadMutation()
  const [markAllActivitiesAsRead] = useMarkAllActivitiesAsReadMutation()
  const [markTicketAsRead] = useMarkTicketAsReadMutation()
  const [markingAll, setMarkingAll] = useState(false)

  const groups = useMemo(() => groupByDay(list.items), [list.items])

  const handleItemClick = async (activity: MerchantActivityResponse) => {
    if (activity.id == null || activity.isRead !== false) {
      return
    }
    list.setRead(activity.id, true)
    try {
      await markActivityAsRead(activity.id).unwrap()
    } catch {
      list.setRead(activity.id, false)
      toastFailure(
        tr('FRONTEND.NOTIFICATIONS_PANEL.MARK_READ_ERROR', "Couldn't update the notification. Please try again.")
      )
    }
  }

  const handleMarkAll = async () => {
    setMarkingAll(true)
    try {
      await Promise.all([
        markAllActivitiesAsRead().unwrap(),
        ...(unreadTickets ?? []).map(ticket => markTicketAsRead(ticket.ticketId).unwrap())
      ])
      list.markAllRead()
    } catch {
      toastFailure(
        tr('FRONTEND.NOTIFICATIONS_PANEL.MARK_READ_ERROR', "Couldn't update the notification. Please try again.")
      )
    } finally {
      setMarkingAll(false)
    }
  }

  return (
    <div className={cn(styles['notifications-panel'], 'notifications-panel')}>
      <div className={cn(styles['notifications-toolbar'], 'notifications-toolbar')}>
        <MMButton
          type='button'
          size='small'
          variant='tertiary'
          caption={tr('FRONTEND.NOTIFICATIONS_PANEL.MARK_ALL_READ', 'Mark all as read')}
          disabled={unreadTotal === 0 || markingAll}
          loading={markingAll}
          onClick={handleMarkAll}
          data-test='notifications-mark-all'
        />
      </div>

      <SupportBlock />

      {list.error ? <ErrorDisplay error={list.error} /> : null}

      {list.isInitialLoading ? (
        <CoreLoader />
      ) : list.items.length === 0 && !list.error ? (
        <div className={cn(styles['notifications-empty'], 'notifications-empty')}>
          <span className={cn(styles['notifications-empty-icon'], 'notifications-empty-icon')}>
            <BellIcon />
          </span>
          <h3 className={cn(styles['notifications-empty-title'], 'notifications-empty-title')}>
            {tr('FRONTEND.NOTIFICATIONS_PANEL.EMPTY_TITLE', 'No notifications yet')}
          </h3>
          <p className={cn(styles['notifications-empty-text'], 'notifications-empty-text')}>
            {tr(
              'FRONTEND.NOTIFICATIONS_PANEL.EMPTY_TEXT',
              "We'll let you know here when something needs your attention."
            )}
          </p>
        </div>
      ) : (
        <>
          {groups.map(([day, activities]) => (
            <section key={day} className={cn(styles['notifications-day'], 'notifications-day')}>
              <h3 className={cn(styles['notifications-day-title'], 'notifications-day-title')}>{day}</h3>
              <div className={cn(styles['notifications-group'], 'notifications-group')}>
                {activities.map(activity => (
                  <NotificationItem key={activity.id} activity={activity} onClick={handleItemClick} />
                ))}
              </div>
            </section>
          ))}

          {list.items.length > 0 && (
            <div className={cn(styles['notifications-pager'], 'notifications-pager')}>
              <span className={cn(styles['notifications-pager-count'], 'notifications-pager-count')}>
                {list.items.length} {tr('FRONTEND.DASHBOARD.TKB_USAGE.OF', 'of')} {list.totalElements}
              </span>
              {list.hasMore && (
                <MMButton
                  type='button'
                  size='small'
                  variant='secondary'
                  caption={tr('FRONTEND.ACTIVITIES.LOAD_MORE_BUTTON', 'Load more')}
                  loading={list.isLoading}
                  onClick={list.loadMore}
                  data-test='notifications-load-more'
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
